import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import type { RouterHistory } from '@tanstack/react-router';
import { isApiError } from '@kidova/api-client';
import { AppShell } from '../components/AppShell';
import { ErrorState, PageSkeleton } from '../components/states';
import { NotFound } from '../components/NotFound';
import { authHint } from '../lib/preferences';
import { q } from '../lib/queries';

const noop = () => undefined;

export interface RouterContext {
  queryClient: QueryClient;
}

type Range = '7d' | '30d' | '90d';
const RANGES = new Set<string>(['7d', '30d', '90d']);
const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

const root = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFound,
});

const login = createRoute({
  getParentRoute: () => root,
  path: '/login',
  validateSearch: (s: Record<string, unknown>) => ({ next: str(s.next) }),
}).lazy(() => import('../pages/auth').then((m) => m.LoginRoute));

const signup = createRoute({
  getParentRoute: () => root,
  path: '/signup',
  validateSearch: (s: Record<string, unknown>) => ({
    plan: str(s.plan),
    period: str(s.period),
  }),
}).lazy(() => import('../pages/signup').then((m) => m.Route));

const cancel = createRoute({ getParentRoute: () => root, path: '/cancel' }).lazy(() =>
  import('../pages/contract').then((m) => m.CancelRoute),
);
const withdraw = createRoute({ getParentRoute: () => root, path: '/withdraw' }).lazy(() =>
  import('../pages/contract').then((m) => m.WithdrawRoute),
);

const devCheckout = createRoute({
  getParentRoute: () => root,
  path: '/dev/checkout/$provider/$reference',
  validateSearch: (s: Record<string, unknown>) => s,
}).lazy(() => import('../pages/dev').then((m) => m.DevCheckoutRoute));
const devPortal = createRoute({
  getParentRoute: () => root,
  path: '/dev/billing-portal/$provider/$customer',
}).lazy(() => import('../pages/dev').then((m) => m.DevPortalRoute));

/** Everything behind the BFF session (§11.9). A reload without the sign-in hint goes straight to
 * /login instead of briefly rendering the shell. */
const app = createRoute({
  getParentRoute: () => root,
  id: '_app',
  component: AppShell,
  beforeLoad: async ({ context, location }) => {
    const toLogin = () =>
      redirect({
        to: '/login',
        search: { next: location.href === '/' ? undefined : location.href },
        throw: true,
      });
    if (!authHint.get()) toLogin();
    try {
      await context.queryClient.query({ ...q.session(), staleTime: 'static' });
    } catch (error) {
      if (isApiError(error) && error.status === 401) {
        authHint.set(false);
        toLogin();
      }
      throw error;
    }
  },
});

const home = createRoute({
  getParentRoute: () => app,
  path: '/',
  loader: async ({ context: { queryClient } }) => {
    void queryClient.query(q.activity('7d')).catch(noop);
    void queryClient.query(q.requests('pending')).catch(noop);
    await queryClient.query({ ...q.home(), staleTime: 'static' });
  },
}).lazy(() => import('../pages/home').then((m) => m.Route));

const onboarding = createRoute({
  getParentRoute: () => app,
  path: '/onboarding',
  validateSearch: (s: Record<string, unknown>) => ({ profile: str(s.profile) }),
}).lazy(() => import('../pages/onboarding').then((m) => m.Route));

const children = createRoute({
  getParentRoute: () => app,
  path: '/children',
  loader: ({ context: { queryClient } }) => queryClient.query({ ...q.home(), staleTime: 'static' }),
}).lazy(() => import('../pages/children').then((m) => m.ChildrenRoute));

export type ChildTab = 'protection' | 'time' | 'video' | 'devices' | 'report';
const TABS = new Set<string>(['protection', 'time', 'video', 'devices', 'report']);
const child = createRoute({
  getParentRoute: () => app,
  path: '/children/$profileId',
  validateSearch: (s: Record<string, unknown>): { tab: ChildTab } => ({
    tab: (TABS.has(String(s.tab)) ? s.tab : 'protection') as ChildTab,
  }),
  loader: async ({ context: { queryClient }, params }) => {
    void queryClient.query(q.billing()).catch(noop);
    await Promise.all([
      queryClient.query({ ...q.profile(params.profileId), staleTime: 'static' }),
      queryClient.query({ ...q.home(), staleTime: 'static' }),
    ]);
  },
}).lazy(() => import('../pages/child').then((m) => m.Route));

const devices = createRoute({
  getParentRoute: () => app,
  path: '/devices',
  loader: ({ context: { queryClient } }) =>
    queryClient.query({ ...q.devices(), staleTime: 'static' }),
}).lazy(() => import('../pages/devices').then((m) => m.Route));

const requests = createRoute({
  getParentRoute: () => app,
  path: '/requests',
  validateSearch: (s: Record<string, unknown>) => ({
    tab: (['pending', 'history', 'reports'].includes(String(s.tab)) ? s.tab : 'pending') as
      'pending' | 'history' | 'reports',
  }),
  loader: async ({ context: { queryClient } }) => {
    await Promise.all([
      queryClient.query({ ...q.requests('all'), staleTime: 'static' }),
      queryClient.query({ ...q.home(), staleTime: 'static' }),
    ]);
  },
}).lazy(() => import('../pages/requests').then((m) => m.Route));

const reports = createRoute({
  getParentRoute: () => app,
  path: '/reports',
  validateSearch: (s: Record<string, unknown>) => ({
    profile: str(s.profile),
    range: (RANGES.has(String(s.range)) ? s.range : '7d') as Range,
  }),
  loader: ({ context: { queryClient } }) => queryClient.query({ ...q.home(), staleTime: 'static' }),
}).lazy(() => import('../pages/reports').then((m) => m.Route));

const vpn = createRoute({
  getParentRoute: () => app,
  path: '/vpn',
  loader: ({ context: { queryClient } }) => queryClient.query({ ...q.home(), staleTime: 'static' }),
}).lazy(() => import('../pages/vpn').then((m) => m.Route));

const billing = createRoute({
  getParentRoute: () => app,
  path: '/billing',
  validateSearch: (s: Record<string, unknown>) => ({
    checkout: str(s.checkout),
    plan: str(s.plan),
    period: str(s.period),
  }),
  loader: async ({ context: { queryClient } }) => {
    const session = await queryClient.query({ ...q.session(), staleTime: 'static' });
    void queryClient.query(q.invoices()).catch(noop);
    await Promise.all([
      queryClient.query({ ...q.billing(), staleTime: 'static' }),
      queryClient.query({ ...q.plans(session.family.country), staleTime: 'static' }),
    ]);
  },
}).lazy(() => import('../pages/billing').then((m) => m.Route));

const privacy = createRoute({
  getParentRoute: () => app,
  path: '/privacy',
  loader: ({ context: { queryClient } }) =>
    queryClient.query({ ...q.privacy(), staleTime: 'static' }),
}).lazy(() => import('../pages/privacy').then((m) => m.Route));

const settings = createRoute({
  getParentRoute: () => app,
  path: '/settings',
  loader: async ({ context: { queryClient } }) => {
    void queryClient.query(q.sessions()).catch(noop);
    void queryClient.query(q.passkeys()).catch(noop);
    await Promise.all([
      queryClient.query({ ...q.family(), staleTime: 'static' }),
      queryClient.query({ ...q.members(), staleTime: 'static' }),
    ]);
  },
}).lazy(() => import('../pages/settings').then((m) => m.Route));

const tree = root.addChildren([
  login,
  signup,
  cancel,
  withdraw,
  devCheckout,
  devPortal,
  app.addChildren([
    home,
    onboarding,
    children,
    child,
    devices,
    requests,
    reports,
    vpn,
    billing,
    privacy,
    settings,
  ]),
]);

/** `history` is set when prerendering (src/prerender.tsx), which must emit no inline script (CSP). */
export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree: tree,
    // Prerendering runs the browser code path in Node, which has no window.origin to read.
    ...(history ? { history, origin: 'https://app.kidova.com' } : {}),
    context: { queryClient },
    defaultPreload: 'intent',
    // Cached data renders at once; the loader only blocks when nothing is cached. A skeleton appears
    // only after 200 ms and then stays at least 400 ms, so a fast answer never flashes one.
    defaultPreloadStaleTime: 0,
    defaultPendingMs: 200,
    defaultPendingMinMs: 400,
    defaultPendingComponent: () => <PageSkeleton />,
    defaultErrorComponent: ({ error, reset }) => <ErrorState error={error} onRetry={reset} />,
    // On the server it would render an inline script, which the CSP blocks; in the browser it
    // renders nothing, so turning it off there keeps prerendered markup and hydration identical.
    scrollRestoration: !history,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
